import {
  Box,
  Button,
  FormLabel,
  Grid,
  Stack,
  Typography
} from "@mui/material";
import { toast } from "react-toastify";
import LinkWithQuery from "../components/helpers/LinkWithQuery.js";
import TextField from "../components/inputs/TextField.js";
import LoadingComponent from "../components/loaders/LoadingComponent.js";
import LoadingState from "../components/loaders/LoadingState.js";
import Page from "../components/ui/Page.js";
import { GENERIC_ERROR } from "../constants/messages/errors.js";
import { useContactMutation } from "../features/contact/api/contactApi.js";
import useOffer from "../features/offer/hooks/useOffer.js";
import { formatPhoneNumber } from "../utils/formatPhoneNumber.js";

const ContactPage = () => {
  const { isLoading: loadingOffer, Reservation } = useOffer();
  const { FirstName, LastName, Email, Phone } = Reservation || {};

  const [contact, { isLoading, isSuccess }] = useContactMutation();

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData(e.target);

    const payload = Object.fromEntries(formData.entries());

    try {
      await contact(payload).unwrap();
      window.scrollTo(0, 0);
    } catch (error) {
      toast.error(GENERIC_ERROR);
    }
  };
  return (
    <Page
      title="Contact"
      pb={0}
      sx={{
        bgcolor: "background.default",
        paddingBottom: "0 !important",
      }}
    >
      <Grid container>
        <Box flex={1} sx={{ mx: 0, px: 3.5, py: 6.5 }}>
          <Stack
            gap={2}
            sx={{
              bgcolor: "background.paper",
              p: 4,
              borderRadius: 3.5,
              textWrap: "balance",
            }}
          >
            <Typography variant="h3">Support</Typography>
            <Typography>
              We’ve put together a comprehensive knowledge base for our guests
              to find the answers they seek.
            </Typography>
            <Typography
              color="primary"
              fontWeight="bold"
              to="/faqs"
              component={LinkWithQuery}
            >
              Frequently Asked Questions
            </Typography>
            <Typography fontWeight="bold">Email Support:</Typography>
            <Typography>
              If you still can’t find what you’re looking for, please send us a
              message using the form or email directly to{" "}
              <Typography
                href={`mailto:${process.env.REACT_APP_SUPPORT_EMAIL}`}
                component="a"
              >
                {process.env.REACT_APP_SUPPORT_EMAIL}
              </Typography>
              .
            </Typography>
            <Typography fontWeight="bold">Support Hours:</Typography>
            <Typography>Monday-Sunday, 9AM to 5PM EST</Typography>
          </Stack>
        </Box>
        <Box
          flex={1}
          sx={{
            mx: 0,
            bgcolor: "background.paper",
            px: 6,
            pt: 6.25,
            pb: 3.75,
            py: { lg: 11.25 },
          }}
        >
          <Stack component="form" gap={2} onSubmit={handleSubmit}>
            {loadingOffer || (Reservation && !FirstName) ? (
              <>
                <LoadingComponent width={300} height={50} />
                <LoadingComponent
                  variant="rounded"
                  count={2}
                  width="stretch"
                  height={60}
                />
                <LoadingComponent
                  variant="rounded"
                  width="stretch"
                  height={250}
                />
                <LoadingComponent variant="rounded" height={60} width={200} />
              </>
            ) : isSuccess ? (
              <Box>
                <Typography fontWeight="bold">Success! </Typography>
                <Typography>Your message has been received.</Typography>
              </Box>
            ) : (
              <>
                {isLoading && <LoadingState />}
                <Typography variant="h4" fontSize={24}>
                  {FirstName && `Hi ${FirstName}!`}
                </Typography>
                {!Reservation && (
                  <>
                    <TextField
                      label="Full Name*"
                      name="fullname"
                      autoComplete="name"
                      defaultValue={
                        FirstName ? `${FirstName} ${LastName ?? ""}` : ""
                      }
                      required
                    />
                    <TextField
                      label="Rental Company (if known)"
                      name="rentalcompany"
                    />
                    <TextField
                      label="Reservation Number (if known)"
                      name="reservationnumber"
                    />
                  </>
                )}
                <TextField
                  label="Email Address *"
                  name="email"
                  type="email"
                  defaultValue={Email}
                  autoComplete="email"
                  required
                />
                <TextField
                  label="Mobile Phone *"
                  name="phone"
                  type="tel"
                  defaultValue={formatPhoneNumber(Phone)}
                  autoComplete="tel"
                  useMask="(###) ###-####"
                  minLength={14}
                  required
                />
                <TextField
                  label="How can we help? *"
                  name="message"
                  rows={4}
                  multiline
                  required
                />
                <FormLabel>* Required field</FormLabel>
                <Button variant="contained" type="submit" sx={{ width: 212 }}>
                  Submit
                </Button>
              </>
            )}
          </Stack>
        </Box>
      </Grid>
    </Page>
  );
};

export default ContactPage;
